import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Target,
  CheckCircle,
  Package,
  Wrench,
  Receipt,
  Layers
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import categoryMasterService from '../../services/categoryMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const CategoryMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
    inactiveCategories: 0,
    productType: 0,
    serviceType: 0,
    expenseType: 0,
    topLevelCategories: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [categoriesResponse, statsResponse] = await Promise.all([
        categoryMasterService.getAllCategories(),
        categoryMasterService.getCategoryStats()
      ])
      
      if (categoriesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (categoriesResponse.data || []).map(category => ({
          ...category,
          id: category._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (categoriesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No category records found. Create your first category!')
        }
      } else {
        toast.error(categoriesResponse.message || 'Failed to load categories')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading category master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view categories')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load category master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getCategoryTypeBadgeClass = (type) => {
    const typeMap = {
      'Product': 'active',
      'Service': 'warning',
      'Expense': 'pending',
      'Asset': 'active',
      'General': 'inactive',
      'Other': 'inactive'
    }
    return typeMap[type] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'categoryCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'categoryName',
      header: 'Category Name',
      cell: ({ getValue, row }) => {
        const level = row.original.categoryLevel || 1
        const indent = (level - 1) * 20
        return (
          <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word', paddingLeft: `${indent}px` }}>
            <span className="description-text">{getValue() || '-'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'categoryType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getCategoryTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'parentCategory',
      header: 'Parent Category',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>
          {row.original.parentCategory?.categoryName || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'budget.annualBudget',
      header: 'Annual Budget',
      cell: ({ row }) => {
        const budget = row.original.budget?.annualBudget || 0
        const currency = row.original.budget?.currency || 'INR'
        return (
          <div style={{ textAlign: 'right', fontWeight: '600' }}>
            {budget > 0 ? new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(budget) : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/category-master/form')
  }

  const handleEdit = (categoryData) => {
    navigate('/procurement/category-master/edit', { state: { categoryData } })
  }

  const handleView = (categoryData) => {
    navigate('/procurement/category-master/view', { state: { categoryData } })
  }

  const handleDelete = (categoryData) => {
    setDeleteItem(categoryData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting category...')
      const response = await categoryMasterService.deleteCategory(deleteItem._id)
      if (response.success) {
        toast.success('Category deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete category')
      }
    } catch (error) {
      console.error('Error deleting category:', error)
      if (error.response?.status === 404) {
        toast.error('Category not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Cannot delete category with child categories')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this category')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete category. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Masters', path: '/procurement?tab=masters' },
    { label: 'Categories', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Categories</h1>
          <p>Purchase categories and classifications</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Category
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCategories || 0}</div>
          <div className="metric-title">Total Categories</div>
          <div className="metric-subtitle">All classifications</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeCategories || 0}</div>
          <div className="metric-title">Active Categories</div>
          <div className="metric-subtitle">In use</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Product</span>
            </div>
          </div>
          <div className="metric-value">{stats.productType || 0}</div>
          <div className="metric-title">Product Type</div>
          <div className="metric-subtitle">Product categories</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Service</span>
            </div>
          </div>
          <div className="metric-value">{stats.serviceType || 0}</div>
          <div className="metric-title">Service Type</div>
          <div className="metric-subtitle">Service categories</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Receipt size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Expense</span>
            </div>
          </div>
          <div className="metric-value">{stats.expenseType || 0}</div>
          <div className="metric-title">Expense Type</div>
          <div className="metric-subtitle">Expense categories</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Layers size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Root</span>
            </div>
          </div>
          <div className="metric-value">{stats.topLevelCategories || 0}</div>
          <div className="metric-title">Top Level</div>
          <div className="metric-subtitle">Root categories</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="category-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete category "${deleteItem?.categoryName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default CategoryMaster

